import { IAppointment, ICustomer, IService, RNDService } from '../API/GoMoto';
import { AccountType, Timezone } from '../API/Enums';
import dayjs from 'dayjs';
import { useLocale } from '../context/Localization/localization';
import { minEstCost, rndMinEstCost } from './minEstCost';
import { useKioskState } from '../context/Kiosk/kiosk';
import { textCapitalize } from './textCapitalize';
import { Locales } from '../context/Localization/localization.types';
import { GoMotoFunction } from '../API/GoMoto.types';
import 'dayjs/locale/es-us';
import 'dayjs/locale/fr';

/**
 * Gets the appropriate display name for the appointment or customer. In order of preference: business name, first name, last name.
 * @param appointmentOrCustomer The appointment or customer.
 */
export const getDisplayName = (appointmentOrCustomer: IAppointment | ICustomer) => {
    const name =
        (appointmentOrCustomer.accountType === AccountType.business && appointmentOrCustomer.businessName) ||
        appointmentOrCustomer.firstName ||
        appointmentOrCustomer.lastName ||
        '';

    return textCapitalize(name, appointmentOrCustomer.accountType === AccountType.business);
};

/**
 * Gets the appropriate full display name for the appointment or customer.
 * @param appointmentorCustomer The appointment or customer.
 * @param lastFirst Formats name as Last Name, First Name
 */
export const getFullDisplayName = (appointmentorCustomer: IAppointment | ICustomer, lastFirst: boolean = true) => {
    const name =
        (appointmentorCustomer.accountType === AccountType.business && appointmentorCustomer.businessName) ||
        (appointmentorCustomer.firstName === null && appointmentorCustomer.lastName) ||
        (lastFirst && [appointmentorCustomer.lastName, appointmentorCustomer.firstName].join(', ')) ||
        (!lastFirst && `${appointmentorCustomer.firstName} ${appointmentorCustomer.lastName}`) ||
        '';

    return textCapitalize(name, appointmentorCustomer.accountType === AccountType.business || appointmentorCustomer.firstName === null);
};

/**
 * Formats the appointment date/time according to the provided format.
 * @param appointment The appointment
 * @param format A valid date/time format
 */
export const formatAppointmentDate = (
    appointment: IAppointment,
    locale: Locales,
    format = 'dddd, MMMM D, YYYY | h:mm A',
    dealerTimeConvert: (date: dayjs.Dayjs) => dayjs.Dayjs = (date) => date,
) => {
    const apptDate =
        typeof appointment.appointmentDateTime === 'string' ? dayjs.utc(appointment.appointmentDateTime)
            .local() : dayjs(appointment.appointmentDateTime);
    const formatted = dealerTimeConvert(apptDate)
        .locale(locale === Locales.es ? 'es-us' : locale === Locales.fr ? 'fr' : 'en')
        .format(format);

    return formatted.charAt(0)
        .toUpperCase() + formatted.slice(1);
};

export interface FormatAppointmentDateProps {
    checkInCompleteFormat?: boolean;
}

export type FormatAppointmentDate = GoMotoFunction<
    [FormatAppointmentDateProps?],
    GoMotoFunction<[IAppointment, { [key in Locales]?: string }?], string>
>;

/**
 * Returns a function that formats the appointment date/time according to the provided format and current locale.
 */
export const useFormatAppointmentDate: FormatAppointmentDate = (props = {}) => {
    const { checkInCompleteFormat = false } = props;

    const locale = useLocale();
    const {
        dealer: { timezone },
    } = useKioskState();

    /**
     * Formats the appointment date/time according to the provided format.
     * @param appointment The appointment
     * @param formats Valid date/time formats for each locale
     */
    return (
        appointment: IAppointment,
        formats: { [key in Locales]?: string } = {
            [Locales.en]: checkInCompleteFormat ? 'MMMM D, YYYY - h:mm A' : 'dddd, MMMM D, YYYY | h:mm A',
            [Locales.es]: checkInCompleteFormat ? 'D [de] MMMM [de] YYYY - h:mm A' : 'dddd D [de] MMMM [de] YYYY | h:mm A',
            [Locales.fr]: checkInCompleteFormat ? 'D MMMM YYYY à H [h] mm' : 'dddd D MMMM YYYY à H [h] mm',
        },
    ) => {
        if (locale === Locales.fr) {
            return formatAppointmentDate(appointment, Locales.fr, formats[Locales.fr], (date) => date.tz(Timezone[timezone]))
                .toLowerCase();
        }
        return formatAppointmentDate(appointment, locale, formats[locale in formats ? locale : Locales.en], (date) => date.tz(Timezone[timezone]));
    };
};

/**
 * Returns true when a mobile check-in was started (created) more than 2 calendar days ago
 * and therefore should not be resumed. When `created` is missing there is no start date
 * to compare against, so the check-in is treated as expired (fail closed) rather than
 * risk resuming a check-in that would fall outside the dashboard window and vanish from
 * the Summary Dashboard drilldown.
 * @param created The appointment's created/started date.
 */
export const isMobileCheckInResumptionExpired = (created?: Date | string): boolean => {
    if (created) {
        return dayjs()
            .startOf('day')
            .diff(dayjs(created)
                .startOf('day'), 'day') > 2;
    }
    else {
        return true;
    }
};

export const getIsInformationMissing = (appointment: IAppointment) => {
    const isMissingInfo = !((appointment.firstName && appointment.lastName) || appointment.businessName);

    return isMissingInfo;
};

/**
 * Gets the services confirmed data that will be sent to the UpdateAppointment
 */
export const getUpdateAptServices = (services: IService[]) =>
    services.map((s) => ({
        id: s.id,
        comment: s.comment,
        price: minEstCost(s) ?? undefined,
        priceLow: s.priceLow,
        priceHigh: s.priceHigh,
        spgPricing: s.useSpgPricing ? s.spgPricing : undefined,
    }));

export const getUpdateAptRndServices = (services: RNDService[]) =>
    services.map((rnd) => ({
        ...rnd,
        price: rndMinEstCost(rnd) ?? undefined,
        linkedService: rnd.linkedService
            ? {
                ...rnd.linkedService,
                spgPricing: rnd.linkedService.useSpgPricing ? rnd.linkedService.spgPricing : undefined,
            }
            : rnd.linkedService,
    }));
